import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { Case } from "../../models/Case";
import JobsRepository from "../../repositories/JobsRepository";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import ConfirmMigrationEntitiesExportToCsvModal, {CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS} from "../common/ConfirmMigrationEntitiesExportToCsvModal";
import { cn } from "@/lib/utils";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Trash2 } from "lucide-react";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface CreateExportCasesToCsvTableProps {
  organizationId: string;
  newCasesForExport: Case[];
  setNewCasesForExport: React.Dispatch<React.SetStateAction<Case[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CreateExportCasesToCsvTable: React.FC<CreateExportCasesToCsvTableProps> = ({
  organizationId,
  newCasesForExport,
  setNewCasesForExport,
  userRole,
  isAssignedUser
}: CreateExportCasesToCsvTableProps) => {
  const requiresReason = useRequiresReason();
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [exportFilesData, setExportFilesData] = useState<boolean>(false);
  const [exportReason, setExportReason] = useState<string | undefined>("");
  const allRows = [...newCasesForExport];
  const newIds = new Set(newCasesForExport.map((c) => c.id));
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  return (
    <div className="flex flex-col gap-4 pt-4 max-h-[40%] min-h-[40%]">
      <div className="flex flex-col gap-2">
        {requiresReason ? (
          <ActionConfirmationModal
            actionConfig={ACTION_CONFIGS.EXPORT_CASE}
            onConfirm={(reason) => {
              setConfirmDeletionModalOpen(true)
              setExportReason(reason);
            }}
          >
            <Button disabled={!(newCasesForExport.length > 0)}>
              Export
            </Button>
          </ActionConfirmationModal>
        ) : (
          <Button
            disabled={!(newCasesForExport.length > 0)}
            onClick={() => setConfirmDeletionModalOpen(true)}
          >
            Export
          </Button>
        )}
        <div className="flex justify-between items-center">
          <Label className="text-sm">Export files data</Label>
          <Checkbox
            checked={exportFilesData}
            onCheckedChange={(checked) => setExportFilesData(checked === true)}
          />
        </div>
      </div>
      <div className={drawerTableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%] min-w-[48px] text-center" />

              <TableHead className="w-[40%]">
                Case Number
              </TableHead>

              <TableHead />
            </TableRow>
          </TableHeader>

          <TableBody>
            {allRows.map((deleteCaseJob) => {
              return (
                <TableRow
                  key={deleteCaseJob.id}
                  className={cn(newIds.has(deleteCaseJob.id) && "bg-primary/10")}
                >
                  <TableCell className="text-center w-[120px]" />
                  <TableCell>
                    <span className="text-sm">
                      {deleteCaseJob.caseNumber}
                    </span>
                  </TableCell>
                  <TableCell className="flex justify-end items-center">
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Cancel"
                      className="text-destructive"
                      onClick={() => {
                        setNewCasesForExport((prevRows) =>
                          prevRows.filter(
                            (entity) => entity.id !== deleteCaseJob.id
                          )
                        );
                      }}
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesExportToCsvModal
          open={confirmDeletionModalOpen}
          entityKeys={newCasesForExport.map((c) => c.caseNumber)}
          exportFilesData={exportFilesData}
          config={CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS.CASES}
          onClose={() => {
            setConfirmDeletionModalOpen(false);
          }}
          onConfirm={async () => {
            await jobsRepository.startCsvGenerationFromCases(
              organizationId,
              newCasesForExport.map((c) => ({ id: c.id, caseNumber: c.caseNumber })),
              exportFilesData,
              exportReason
            );
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_CASES, organizationId] });
            setNewCasesForExport([]);
            setExportReason(undefined);
            setConfirmDeletionModalOpen(false);
            toast.success("Export started");
          }}
        />
      ) : (
        <></>
      )}
    </div>
  );
};

export default CreateExportCasesToCsvTable;
